import { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, FileText, Users, Clock, Target, Zap, AlertCircle, CheckCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsService, RealAnalytics as AnalyticsDataType } from '../../services/analyticsService';
import { SEO } from '../shared/SEO';

export function AnalyticsDashboard() {
  const { invoices, clients } = useApp();
  const { currentWorkspace } = useAuth();
  const [analytics, setAnalytics] = useState<AnalyticsDataType | null>(null);
  const [insights, setInsights] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('12months');
  const [isLoading, setIsLoading] = useState(true);

  const currency = currentWorkspace?.settings.currency || 'QAR';
  const currencySymbol = getCurrencySymbol(currency);

  useEffect(() => {
    const calculateAnalytics = () => {
      setIsLoading(true);
      try {
        const realAnalytics = analyticsService.calculateRealAnalytics(invoices, clients);
        const generatedInsights = analyticsService.generateInsights(realAnalytics);
        
        setAnalytics(realAnalytics);
        setInsights(generatedInsights);
      } catch (error) {
        console.error('Error calculating analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    calculateAnalytics();
  }, [invoices, clients]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 rounded"></div>
            <div className="h-80 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to load analytics</h3>
          <p className="text-gray-500">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };



  return (
    <>
      <SEO
        title="Analytics"
        description="Analyze your freelance business performance, revenue, and trends with FreelancePay analytics."
        keywords={["analytics", "business performance", "freelance", "FreelancePay"]}
      />
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Real Analytics</h2>
            <p className="text-gray-600">Comprehensive insights into your business performance</p>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="30days">Last 30 Days</option>
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="12months">Last 12 Months</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {/* AI Insights */}
        {insights.length > 0 && (
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white">
            <div className="flex items-center mb-4">
              <Zap className="h-6 w-6 mr-2" />
              <h3 className="text-xl font-bold">AI-Powered Insights</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {insights.map((insight, index) => (
                <div key={index} className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
                  <p className="text-sm">{insight}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(analytics.totalRevenue)}</p>
                <div className="flex items-center mt-2">
                  {analytics.revenueGrowth >= 0 ? (
                    <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm font-medium ${analytics.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analytics.revenueGrowth >= 0 ? '+' : ''}{analytics.revenueGrowth.toFixed(1)}%
                  </span>
                  <span className="text-sm text-gray-500 ml-1">vs last month</span>
                </div>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Invoice Value</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(analytics.averageInvoiceValue)}</p>
                <div className="flex items-center mt-2">
                  <Target className="h-4 w-4 text-blue-500 mr-1" />
                  <span className="text-sm text-gray-500">
                    Industry avg: {formatCurrency(analytics.industryComparison.averageInvoiceValue)}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.conversionRate.toFixed(1)}%</p>
                <div className="flex items-center mt-2">
                  <span className={`text-sm font-medium ${
                    analytics.industryComparison.yourPerformance === 'above_average' ? 'text-green-600' :
                    analytics.industryComparison.yourPerformance === 'average' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {analytics.industryComparison.yourPerformance.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Payment Time</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.averagePaymentTime.toFixed(0)} days</p>
                <div className="flex items-center mt-2">
                  <Clock className="h-4 w-4 text-orange-500 mr-1" />
                  <span className="text-sm text-gray-500">
                    Industry avg: {analytics.industryComparison.averagePaymentTime} days
                  </span>
                </div>
              </div>
              <div className="p-3 bg-orange-100 rounded-xl">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Trend */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analytics.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${currencySymbol}${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => [formatCurrency(value as number), 'Revenue']} />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#059669" 
                  fill="#059669"
                  fillOpacity={0.1}
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Invoice Status Distribution */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status Distribution</h3>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Paid', value: analytics.paidInvoices, color: '#059669' },
                      { name: 'Pending', value: analytics.pendingInvoices, color: '#3b82f6' },
                      { name: 'Overdue', value: analytics.overdueInvoices, color: '#ef4444' },
                      { name: 'Draft', value: analytics.draftInvoices, color: '#6b7280' },
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {[
                      { name: 'Paid', value: analytics.paidInvoices, color: '#059669' },
                      { name: 'Pending', value: analytics.pendingInvoices, color: '#3b82f6' },
                      { name: 'Overdue', value: analytics.overdueInvoices, color: '#ef4444' },
                      { name: 'Draft', value: analytics.draftInvoices, color: '#6b7280' },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Monthly Performance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="invoices" fill="#3b82f6" name="Invoices" />
                <Bar dataKey="paidInvoices" fill="#059669" name="Paid Invoices" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Cash Flow Projection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cash Flow Projection</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.cashFlowProjection}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => `${currencySymbol}${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => [formatCurrency(value as number), 'Amount']} />
                <Line 
                  type="monotone" 
                  dataKey="projected" 
                  stroke="#059669" 
                  strokeWidth={3}
                  name="Projected Revenue"
                />
                <Line 
                  type="monotone" 
                  dataKey="outstanding" 
                  stroke="#f59e0b" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Outstanding Amount"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Clients */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Clients by Revenue</h3>
          <div className="space-y-4">
            {analytics.topClients.slice(0, 5).map((clientData, index) => (
              <div key={clientData.client.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-green-600 font-bold">#{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{clientData.client.name}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>{clientData.invoiceCount} invoices</span>
                      <span>Avg: {formatCurrency(clientData.averageValue)}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        clientData.paymentHistory === 'excellent' ? 'bg-green-100 text-green-800' :
                        clientData.paymentHistory === 'good' ? 'bg-blue-100 text-blue-800' :
                        clientData.paymentHistory === 'average' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {clientData.paymentHistory}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatCurrency(clientData.revenue)}</p>
                  {clientData.totalOutstanding > 0 && (
                    <p className="text-sm text-orange-600">
                      Outstanding: {formatCurrency(clientData.totalOutstanding)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h3>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {analytics.recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className={`p-2 rounded-full ${
                  activity.type === 'invoice_paid' ? 'bg-green-100' :
                  activity.type === 'invoice_sent' ? 'bg-blue-100' :
                  activity.type === 'invoice_created' ? 'bg-purple-100' :
                  'bg-gray-100'
                }`}>
                  {activity.type === 'invoice_paid' && <CheckCircle className="h-4 w-4 text-green-600" />}
                  {activity.type === 'invoice_sent' && <FileText className="h-4 w-4 text-blue-600" />}
                  {activity.type === 'invoice_created' && <FileText className="h-4 w-4 text-purple-600" />}
                  {activity.type === 'client_added' && <Users className="h-4 w-4 text-gray-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                  <p className="text-xs text-gray-500">{new Date(activity.date).toLocaleDateString()}</p>
                </div>
                {activity.amount && (
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(activity.amount)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function getCurrencySymbol(currency: string): string {
  const symbols: { [key: string]: string } = {
    'USD': '$', 'EUR': '€', 'GBP': '£', 'QAR': 'QAR ', 'AED': 'AED ', 'SAR': 'SAR ',
    'INR': '₹', 'JPY': '¥', 'CNY': '¥', 'CAD': 'C$', 'AUD': 'A$', 'CHF': 'CHF ',
    'SEK': 'kr', 'NOK': 'kr', 'DKK': 'kr', 'PLN': 'zł', 'CZK': 'Kč', 'HUF': 'Ft',
    'RUB': '₽', 'BRL': 'R$', 'MXN': '$', 'ZAR': 'R', 'KRW': '₩', 'SGD': 'S$',
    'HKD': 'HK$', 'NZD': 'NZ$', 'THB': '฿', 'MYR': 'RM', 'PHP': '₱', 'IDR': 'Rp',
    'VND': '₫', 'TRY': '₺', 'ILS': '₪', 'EGP': 'E£', 'NGN': '₦', 'KES': 'KSh',
    'GHS': 'GH₵', 'MAD': 'MAD ', 'TND': 'TND ', 'DZD': 'DZD ', 'LBP': 'L£',
    'JOD': 'JD', 'KWD': 'KD', 'BHD': 'BD', 'OMR': 'OMR '
  };
  return symbols[currency] || currency + ' ';
}